import React, { useState, useEffect, useMemo } from 'react';
import { useStore } from '../store/useStore';
import {
  getDeletedSheets,
  restoreSheet,
  permanentDeleteSheet,
  permanentDeleteSheetsBulk,
  getSheets,
  getVersionSets,
} from '../utils/api';
import toast from 'react-hot-toast';
import styles from './DeletedSheets.module.css';

// ─── Helpers ────────────────────────────────────────────────────────────────

// Sheets without a version_set get bucketed under this pseudo-key so they're
// still permanently-deletable from the trash UI.
const NO_VS_KEY = '__no_version_set__';

function groupByVersionSet(deletedSheets) {
  const groups = new Map();
  for (const sheet of deletedSheets) {
    const key = sheet.version_set_id || NO_VS_KEY;
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        version_set_id:   sheet.version_set_id || null,
        version_set_name: sheet.version_set_name || '(no version set)',
        sheets:           [],
      });
    }
    groups.get(key).sheets.push(sheet);
  }
  // Server returns sheets ordered by deleted_at DESC, so sheets[0] inside each
  // group is the most-recent deletion in that group. Order groups by their
  // most-recent deletion so the freshest activity is at the top.
  return Array.from(groups.values()).sort((a, b) => {
    const aLatest = a.sheets[0]?.deleted_at || '';
    const bLatest = b.sheets[0]?.deleted_at || '';
    return bLatest.localeCompare(aLatest);
  });
}

// ─── Component ──────────────────────────────────────────────────────────────
// Body-only — rendered as the "Deleted" tab inside `VersionSetsModal`. The
// surrounding backdrop / header / close button live in the parent so this
// component is just the contents of the trash list.

export default function DeletedSheets() {
  const { project, setSheets, setVersionSets } = useStore();
  const [deleted, setDeleted]               = useState([]);
  const [loading, setLoading]               = useState(true);
  const [working, setWorking]               = useState(null);    // sheet id being operated on (per-row)
  const [confirmPerm, setConfirmPerm]       = useState(null);    // sheet id pending per-row perm-delete confirm
  const [expandedGroups, setExpandedGroups] = useState(new Set()); // group keys currently expanded
  const [groupBusy, setGroupBusy]           = useState(null);    // group key whose bulk action is running
  const [confirmGroupKey, setConfirmGroupKey] = useState(null);  // group key pending bulk perm-delete confirm

  useEffect(() => {
    if (!project) return;
    getDeletedSheets({ project_id: project.id })
      .then(setDeleted)
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  }, [project?.id]);

  const groups = useMemo(() => groupByVersionSet(deleted), [deleted]);

  function toggleGroup(key) {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function handleRestore(id) {
    setWorking(id);
    try {
      await restoreSheet(id);
      setDeleted((prev) => prev.filter((s) => s.id !== id));
      const updated = await getSheets({ project_id: project.id });
      setSheets(updated);
      toast.success('Sheet restored');
    } catch (err) {
      toast.error(err.message || 'Restore failed');
    } finally {
      setWorking(null);
    }
  }

  async function handlePermanentDelete(id) {
    setWorking(id);
    try {
      await permanentDeleteSheet(id);
      setDeleted((prev) => prev.filter((s) => s.id !== id));
      toast.success('Sheet permanently deleted');
    } catch (err) {
      toast.error(err.message || 'Delete failed');
    } finally {
      setWorking(null);
      setConfirmPerm(null);
    }
  }

  async function handleBulkDelete(group) {
    const sheetIds = group.sheets.map((s) => s.id);
    setGroupBusy(group.key);
    try {
      const result = await permanentDeleteSheetsBulk(sheetIds);
      // Drop everything we asked to delete from local state — even if the
      // server reports `skipped_count > 0` (already-gone IDs from a prior
      // partial run), they're definitely not in the trash anymore.
      setDeleted((prev) => prev.filter((s) => !sheetIds.includes(s.id)));
      if (result.deleted_version_set_count > 0) {
        try {
          const vss = await getVersionSets(project.id);
          setVersionSets(vss);
        } catch (_) { /* non-fatal */ }
      }
      const skipNote = result.skipped_count > 0
        ? ` (${result.skipped_count} were already gone)`
        : '';
      toast.success(
        result.deleted_version_set_count > 0
          ? `Permanently deleted ${result.deleted_sheet_count} sheet(s) and ${result.deleted_version_set_count} empty version set(s)${skipNote}`
          : `Permanently deleted ${result.deleted_sheet_count} sheet(s)${skipNote}`
      );
    } catch (err) {
      toast.error(err.message || 'Bulk delete failed');
      // Refresh from the server so stale rows don't keep producing
      // failures on retry — a previous timed-out attempt may have
      // committed the DB delete even though R2 cleanup didn't finish.
      try {
        const fresh = await getDeletedSheets({ project_id: project.id });
        setDeleted(fresh);
      } catch (_) { /* non-fatal */ }
    } finally {
      setGroupBusy(null);
      setConfirmGroupKey(null);
    }
  }

  if (loading) {
    return <div className={styles.loading}>Loading…</div>;
  }
  if (deleted.length === 0) {
    return (
      <div className={styles.empty}>
        <p className={styles.emptyText}>No deleted sheets</p>
        <p className={styles.emptyHint}>Sheets you delete will appear here and can be restored.</p>
      </div>
    );
  }

  return (
    <>
      <p className={styles.hint}>
        Sheets are grouped by the version set they were uploaded with. Expand a set to restore or
        permanently delete individual sheets, or use <strong>Permanently delete all</strong> on a
        set to wipe its entire trash. Empty version sets are removed automatically.
      </p>

      {groups.map((group) => {
                const isExpanded = expandedGroups.has(group.key);
                const isBusy     = groupBusy === group.key;
                const isConfirm  = confirmGroupKey === group.key;
                return (
                  <div key={group.key} className={`${styles.groupCard} ${isBusy ? styles.rowWorking : ''}`}>
                    <div
                      className={styles.groupHeader}
                      onClick={() => toggleGroup(group.key)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleGroup(group.key); } }}
                    >
                      <span className={styles.groupCaret}>{isExpanded ? '▾' : '▸'}</span>
                      <div className={styles.groupTitleBlock}>
                        <div className={styles.groupTitle}>{group.version_set_name}</div>
                        <div className={styles.groupSubtitle}>
                          {group.sheets.length} deleted sheet{group.sheets.length !== 1 ? 's' : ''}
                          {group.sheets[0]?.deleted_at && (
                            <> · most recent {new Date(group.sheets[0].deleted_at).toLocaleDateString()}</>
                          )}
                        </div>
                      </div>
                      <div className={styles.groupActions} onClick={(e) => e.stopPropagation()}>
                        {isConfirm ? (
                          <div className={styles.confirmRow}>
                            <span className={styles.confirmText}>
                              Delete {group.sheets.length} sheet{group.sheets.length !== 1 ? 's' : ''}?
                            </span>
                            <button
                              className={styles.permDeleteBtn}
                              onClick={() => handleBulkDelete(group)}
                              disabled={isBusy}
                            >
                              {isBusy ? '…' : 'Yes, delete all'}
                            </button>
                            <button className={styles.cancelBtn} onClick={() => setConfirmGroupKey(null)}>
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            className={styles.permDeleteAllBtn}
                            onClick={() => setConfirmGroupKey(group.key)}
                            disabled={!!groupBusy || !!working}
                            title="Permanently delete every sheet in this version set"
                          >
                            Permanently delete all
                          </button>
                        )}
                      </div>
                    </div>

                    {isExpanded && (
                      <table className={styles.table}>
                        <thead>
                          <tr>
                            <th>NUMBER</th>
                            <th>DISCIPLINE</th>
                            <th>DELETED</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {group.sheets.map((sheet) => (
                            <tr key={sheet.id} className={working === sheet.id ? styles.rowWorking : ''}>
                              <td>
                                <div className={styles.sheetNum}>{sheet.sheet_number}</div>
                                {sheet.title && sheet.title !== sheet.sheet_number && (
                                  <div className={styles.sheetTitle}>{sheet.title}</div>
                                )}
                              </td>
                              <td>{sheet.discipline_name || <span className={styles.dim}>—</span>}</td>
                              <td className={styles.dateCell}>
                                {sheet.deleted_at ? new Date(sheet.deleted_at).toLocaleDateString() : '—'}
                              </td>
                              <td>
                                {confirmPerm === sheet.id ? (
                                  <div className={styles.confirmRow}>
                                    <span className={styles.confirmText}>Permanent delete?</span>
                                    <button
                                      className={styles.permDeleteBtn}
                                      onClick={() => handlePermanentDelete(sheet.id)}
                                      disabled={!!working}
                                    >
                                      {working === sheet.id ? '…' : 'Yes, delete'}
                                    </button>
                                    <button className={styles.cancelBtn} onClick={() => setConfirmPerm(null)}>
                                      Cancel
                                    </button>
                                  </div>
                                ) : (
                                  <div className={styles.actions}>
                                    <button
                                      className={styles.restoreBtn}
                                      onClick={() => handleRestore(sheet.id)}
                                      disabled={!!working || !!groupBusy}
                                    >
                                      {working === sheet.id ? '…' : 'Restore'}
                                    </button>
                                    <button
                                      className={styles.deleteIconBtn}
                                      onClick={() => setConfirmPerm(sheet.id)}
                                      disabled={!!working || !!groupBusy}
                                      title="Permanently delete this sheet"
                                    >
                                      <TrashIcon />
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
        );
      })}
    </>
  );
}

const TrashIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
    <path d="M3 4h10M6 4V3a1 1 0 011-1h2a1 1 0 011 1v1M5 4l.75 8.5a1 1 0 001 .9h2.5a1 1 0 001-.9L11 4"
      stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
